import React, { useEffect, useState } from "react";
import { Box, Typography, Pagination } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import NoOrders from "../../assets/myOrders/NoOrders.svg";
import { fetchOrders } from "../../api/orderFinalAPI";
import { fetchCustomersByEmail } from "../../api/customerAPI";
import DataTable from "../../common/DataTable";
import PageHeader from "../../common/PageHeader";
import {
  StyledCell,
  EmptyContainer,
  EmptyOrdersSVG,
  StyledActionButton,
  StyledLabelCell,
  CardStyle,
} from "./MyOrders.styles";
import { CustomerOrder, Pagination as PaginationType } from "../../types/Order";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import {
  paginationCaptionStyle,
  paginationContainerStyle,
} from "../customerManagement/DisplayCustomers.styles";
import { paginationMobVer } from "../ledger/ledgerStyles";

const MyOrders: React.FC = () => {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [advancePayment, setAdvancePayment] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationType>({
    totalRecords: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const orgId = localStorage.getItem("orgId");
  const emailId = getUserEmailFromLocalStorage() || "";

  useEffect(() => {
    if (!emailId) {
      navigate("/");
      return;
    }

    const loadData = async () => {
      try {
        // Fetching customer data for advance payment status
        const [fetchedOrders, customerData] = await Promise.all([
          fetchOrders({
            emailId,
            page: pagination.currentPage,
            limit: pagination.limit,
            search,
          }),
          fetchCustomersByEmail({ emailId: emailId }),
        ]);
        setOrders(fetchedOrders.orders);
        setPagination({
          totalRecords: fetchedOrders.pagination.totalRecords,
          totalPages: fetchedOrders.pagination.totalPages,
          currentPage: fetchedOrders.pagination.currentPage,
          limit: fetchedOrders.pagination.limit,
        });
        setAdvancePayment(
          customerData?.customer.advancePaymentMandatory || false
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [emailId, pagination.currentPage, pagination.limit, search]);

  const handleCheckDetails = (
    orderID: string,
    paymentStatus: string,
    orderStatus: string
  ) => {
    if (orderStatus === "Cancelled") {
      navigate(`/maltora-customer/order-tracking/${orderID}`);
      return;
    }
    if (advancePayment && paymentStatus !== "Complete") {
      navigate(`/maltora-customer/order-confirmation/${orderID}`);
    } else {
      navigate(`/maltora-customer/order-tracking/${orderID}`);
    }
  };

  const columns = [
    {
      label: <StyledLabelCell>Order Date</StyledLabelCell>,
      field: "date",
      renderCell: (row: CustomerOrder) => (
        <StyledCell>{moment(row.date).format("DD/MM/YYYY")}</StyledCell>
      ),
    },
    {
      label: <StyledLabelCell>Order ID</StyledLabelCell>,
      field: "orderID",
      renderCell: (row: CustomerOrder) => (
        <StyledCell
          sx={{
            color: isMobile ? "#4C93FF" : "#585562",
            cursor: isMobile ? "pointer" : "default",
          }}
          onClick={() => {
            if (isMobile)
              handleCheckDetails(row.orderID, row.payment, row.status);
          }}
        >
          #{row.orderID}
        </StyledCell>
      ),
    },
    {
      label: <StyledLabelCell>Payment Status</StyledLabelCell>,
      field: "payment",
      renderCell: (row: CustomerOrder) => (
        <StyledCell
          sx={{
            color:
              row.payment === "Pending"
                ? "#77757F"
                : row.payment === "Complete"
                  ? "#2FBD6A"
                  : "#FF0F00",
          }}
        >
          {row.payment}
        </StyledCell>
      ),
    },
    {
      label: <StyledLabelCell>Order Status</StyledLabelCell>,
      field: "status",
      renderCell: (row: CustomerOrder) => (
        <StyledCell sx={{ color: "#585562" }}>{row.status}</StyledCell>
      ),
    },
    {
      label: "",
      field: "actions",
      renderCell: (row: CustomerOrder) => {
        const isCancelled = row.status === "Cancelled";
        const showMakePayment =
          advancePayment && row.payment !== "Complete" && !isCancelled;
        return (
          <StyledActionButton
            variant="contained"
            size="small"
            isMobile={isMobile}
            onClick={() =>
              handleCheckDetails(row.orderID, row.payment, row.status)
            }
            data-testid={
              advancePayment && row.payment !== "Complete"
                ? `make-payment-${row.orderID}`
                : `check-details-${row.orderID}`
            }
          >
            {showMakePayment ? "Make Payment" : "Check Details"}
          </StyledActionButton>
        );
      },
    },
  ];

  if (isMobile) columns.pop();

  const handleChangePage = (newPage: number) => {
    if (!isMobile) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const { currentPage, limit, totalPages, totalRecords } = pagination;
  const startIndex = (+currentPage - 1) * +limit;
  const endIndex = Math.min(startIndex + +limit, +totalRecords);
  return (
    <Box>
      <PageHeader title="Orders Details" />
      {loading ? (
        <Typography variant="h6" align="center" data-testid="loading-state">
          Loading orders...
        </Typography>
      ) : orders.length === 0 ? (
        <EmptyContainer>
          <EmptyOrdersSVG
            src={NoOrders}
            alt="No Orders"
            data-testid="no-orders-image"
          />
        </EmptyContainer>
      ) : (
        <CardStyle>
          <DataTable
            columns={columns}
            data={orders}
            emptyStateMessage="No orders available."
            rowsPerPage={5}
            rowStyle={(index) => ({
              borderBottom:
                isMobile || index === orders.length - 1
                  ? "none"
                  : "1px solid rgba(224, 224, 224, 1)",
            })}
          />
        </CardStyle>
      )}
      <Box sx={paginationContainerStyle}>
        <Typography variant="subtitle2" sx={paginationCaptionStyle}>
          Showing data {startIndex + 1} to {endIndex} of {totalRecords} entries
        </Typography>
        <Pagination
          count={totalPages}
          page={+currentPage}
          onChange={(event, value) => handleChangePage(value)}
          shape="rounded"
          variant="outlined"
          sx={paginationMobVer(isMobile)}
        />
      </Box>
    </Box>
  );
};

export default MyOrders;
